import {
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    showToast,
    useSubPage,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";

import ManageRoleForm, { FormValues } from "./manage-role/ManageRoleForm";
import { ManageRoleViewSubpage } from "./manage-role/ManageRoleViewSubpage";
import style from "./user-roles.scss";
import { exportFile } from "components/export/common";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { AUTH_ROLE_CREATE, AUTH_ROLE_DEFAULT, AUTH_ROLE_DELETE, AUTH_ROLE_EDIT } from "domain/authority";
import { Authority, Targets } from "domain/roles";
import { FLAG_RESTRUCTURED_ROLE_MANAGEMENT } from "services/feature/FeatureFlagService";
import { rolesService } from "services/roles/RolesService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    roleUuid: string;
    name: string;
    description: string;
    isManager: boolean;
    authorities: Authority[];
    targets?: Targets;
    onRoleEdit: () => void;
    isDefault: boolean;
    onRoleDelete: () => void;
}

interface Result {
    title: string;
    message: string;
}

export const UserRolesKebabMenu = (props: Props) => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    const [editRoleFormVisible, setEditRoleFormVisible] = React.useState(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [resultVisible, setResultVisible] = React.useState<boolean>(false);
    const [cloneRoleFormVisible, setCloneRoleFormVisible] = React.useState<boolean>(false);
    const [deleteRoleFormVisible, setDeleteRoleFormVisible] = React.useState<boolean>(false);
    const [okClicked, setOkClicked] = React.useState<boolean>(false);
    const [confirmationState, setConfirmationState] = React.useState<boolean>(true);
    // TODO BCC-7836: remove isRestructuredRoleManagament once the ticket is done
    const isRestructuredRoleManagament = useFeature(FLAG_RESTRUCTURED_ROLE_MANAGEMENT);
    const { setSubPage, closeSubPage } = useSubPage();

    React.useEffect(() => {
        return () => abortControllers.filter((value) => !value.signal.aborted).forEach((value) => value.abort());
    }, []);

    const showResult = (resultToShow: Result) => {
        setResult(resultToShow);
        setResultVisible(true);
    };
    const hideResult = () => {
        setResultVisible(false);
        props.onRoleEdit();
    };

    const showCloneRoleForm = () => {
        usageStatisticsService.sendEvent({
            category: Category.ROLE,
            action: Action.CLONE_ROLE,
        });
        setCloneRoleFormVisible(true);
    };

    const openCloneRoleViewSubpage = () => {
        usageStatisticsService.sendEvent({
            category: Category.ROLE,
            action: Action.CLONE_ROLE,
        });
        setSubPage(
            <ManageRoleViewSubpage
                isViewRole={false}
                name={props.name + " Clone"}
                authorities={props.authorities}
                targets={props.targets}
                isManager={false}
                isEditing={false}
                submitEventHandler={cloneRoleSubmitEventHandler}
                description=""
            />
        );
        setCloneRoleFormVisible(true);
    };

    const openEditRoleViewSubpage = () => {
        setEditRoleFormVisible(true);
        usageStatisticsService.sendEvent({
            category: Category.ROLE,
            action: Action.EDIT_ROLE,
        });
        setSubPage(
            <ManageRoleViewSubpage
                isViewRole={false}
                name={props.name}
                description={props.description}
                authorities={props.authorities}
                targets={props.targets}
                isManager={props.isManager}
                isEditing={true}
                submitEventHandler={editRoleSubmitEventHandler}
            />
        );
    };

    function handleConfirmation() {
        setConfirmationState(
            (document.getElementById("confirmInput") as HTMLInputElement).value !== t("Common.delete").toUpperCase()
        );
    }

    const hideResultAndRedirectToRoot = () => {
        setResultVisible(false);
        props.onRoleDelete();
    };

    const handleRoleDelete = () => {
        setOkClicked(true);
        deleteRoleSubmitEventHandler();
    };

    const exportView = () => {
        usageStatisticsService.sendEvent({
            category: Category.ROLE,
            action: Action.EXPORT_ROLE,
        });
        const exportData = JSON.stringify(getExportData(), null, 4);
        const blob = new Blob([exportData], {
            type: "application/json",
        });
        exportFile(URL.createObjectURL(blob), props.name + ".json");
    };
    const getExportData = () => {
        return {
            name: props.name,
            authorities: props.authorities,
            targets: props.targets || "NONE",
            ...(props.description && props.description.trim() && { description: props.description }),
        };
    };

    const editRoleSubmitEventHandler = async ({
        name,
        description,
        authorities,
        targets,
    }: FormValues): Promise<void> => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await rolesService.editRole(props.roleUuid, name, description, authorities, targets, abortController);
        } catch (e) {
            if (!signal.aborted) {
                setEditRoleFormVisible(false);
                showResult({
                    title: t("ManageRoleForm.edit.failure.title"),
                    message: t("ManageRoleForm.edit.failure.message"),
                });
                logger.error("Failed to edit a role:", e);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setEditRoleFormVisible(false);
        if (isRestructuredRoleManagament) {
            closeSubPage();
            showToast(t("ManageRoleForm.edit.success.message"), "success");
            return;
        }
        showResult({
            title: t("ManageRoleForm.edit.success.title"),
            message: t("ManageRoleForm.edit.success.message"),
        });
    };

    const setDefaultRole = (uuid: string) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        rolesService.setDefault(uuid, abortController).then(() => {
            props.onRoleEdit();
        });
    };

    const editRoleModal = (
        <Modal
            isOpen={editRoleFormVisible}
            hideModal={() => setEditRoleFormVisible(false)}
            modalTitle={t("ManageRoleForm.edit.editRoleTitle")}
        >
            <div className={style.fixedWidthModal}>
                <TextBlock>{t("ManageRoleForm.edit.editRoleSummaryText")}</TextBlock>
                <ManageRoleForm
                    name={props.name}
                    description={props.description}
                    authorities={props.authorities}
                    targets={props.targets}
                    isManager={props.isManager}
                    isEditing={true}
                    submitEventHandler={editRoleSubmitEventHandler}
                />
            </div>
        </Modal>
    );

    const cloneRoleSubmitEventHandler = async ({
        name,
        description,
        authorities,
        targets,
    }: FormValues): Promise<void> => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await rolesService.generateRoles(name, description, authorities, targets, abortController);
        } catch (e) {
            if (!signal.aborted) {
                setCloneRoleFormVisible(false);
                showResult({
                    title: t("ManageRoleForm.failure.title"),
                    message: t("ManageRoleForm.failure.message"),
                });
                logger.error("Failed to clone a role:", e);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setCloneRoleFormVisible(false);
        if (isRestructuredRoleManagament) {
            closeSubPage();
            showToast(t("ManageRoleForm.success.message", { name: name }), "success");
            return;
        }
        showResult({
            title: t("ManageRoleForm.success.title"),
            message: t("ManageRoleForm.success.message", { name: name }),
        });
    };

    const cloneRoleModal = (
        <Modal
            isOpen={cloneRoleFormVisible}
            hideModal={() => setCloneRoleFormVisible(false)}
            modalTitle={t("ManageRoleForm.createRoleTitle")}
        >
            <div className={style.fixedWidthModal}>
                <TextBlock>{t("ManageRoleForm.addUserRoleSummaryText")}</TextBlock>
                <ManageRoleForm
                    name={props.name + " Clone"}
                    authorities={props.authorities}
                    targets={props.targets}
                    isManager={false}
                    isEditing={false}
                    submitEventHandler={cloneRoleSubmitEventHandler}
                />
            </div>
        </Modal>
    );

    const deleteRoleSubmitEventHandler = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await rolesService.deleteRole(props.roleUuid, abortController);
        } catch (e) {
            if (!signal.aborted) {
                setOkClicked(false);
                setDeleteRoleFormVisible(false);
                showResult({
                    title: t("ManageRoleForm.deleteRole.roleDeleted.roleNotDeletedTitle"),
                    message: t("ManageRoleForm.deleteRole.roleDeleted.failureMessage"),
                });
                logger.error("Failed to delete a role", e);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setOkClicked(false);
        setDeleteRoleFormVisible(false);
        showResult({
            title: t("ManageRoleForm.deleteRole.roleDeleted.roleDeletedTitle"),
            message: t("ManageRoleForm.deleteRole.roleDeleted.successMessage", { name: props.name }),
        });
    };

    return (
        <>
            <DropdownMenu
                position={"bottom-start"}
                dropdownOffset={"Xxs"}
                closeOnSelection
                flipOptions={{
                    fallbackPlacements: ["top-start"],
                }}
                testId={testIds.common.primaryView.table.kebabMenu.itself}
            >
                <DropdownTrigger>
                    <IconButton>
                        <Kebab />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent>
                    {userSessionService.userHasAllAuthorities([AUTH_ROLE_CREATE]) && [
                        <>
                            <DropdownMenuItem
                                key={"clone"}
                                onClick={isRestructuredRoleManagament ? openCloneRoleViewSubpage : showCloneRoleForm}
                                testId={testIds.workArea.roles.table.kebabMenu.cloneButton}
                            >
                                {t("Common.clone")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                key={"export"}
                                onClick={exportView}
                                testId={testIds.workArea.roles.table.kebabMenu.exportButton}
                            >
                                {t("Common.export")}
                            </DropdownMenuItem>
                        </>,
                    ]}
                    {userSessionService.userHasAllAuthorities([AUTH_ROLE_EDIT]) && (
                        <DropdownMenuItem
                            onClick={
                                isRestructuredRoleManagament
                                    ? openEditRoleViewSubpage
                                    : () => {
                                          setEditRoleFormVisible(true);
                                          usageStatisticsService.sendEvent({
                                              category: Category.ROLE,
                                              action: Action.EDIT_ROLE,
                                          });
                                      }
                            }
                            testId={testIds.workArea.roles.table.kebabMenu.editButton}
                        >
                            {t("Common.edit")}
                        </DropdownMenuItem>
                    )}
                    {userSessionService.userHasAllAuthorities([AUTH_ROLE_DEFAULT]) && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                if (!props.isDefault) {
                                    usageStatisticsService.sendEvent({
                                        category: Category.ROLE,
                                        action: Action.SET_DEFAULT_ROLE,
                                    });
                                    setDefaultRole(props.roleUuid);
                                }
                                e.preventDefault();
                            }}
                            testId={testIds.workArea.roles.table.kebabMenu.setDefaultButton}
                            className={props.isDefault ? style.disabledKebabMenuItem : undefined}
                            showTooltip
                            value={t("UserRoles.table.itsDefault")}
                        >
                            {t("Common.setDefault")}
                        </DropdownMenuItem>
                    )}
                    {userSessionService.userHasAllAuthorities([AUTH_ROLE_DELETE]) && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                errorStyle
                                onClick={() => {
                                    if (!props.isManager && !props.isDefault) {
                                        setDeleteRoleFormVisible(true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.ROLE,
                                            action: Action.DELETE_ROLE,
                                        });
                                    }
                                }}
                                testId={testIds.workArea.roles.table.kebabMenu.deleteButton}
                                className={props.isDefault || props.isManager ? style.disabledKebabMenuItem : undefined}
                                showTooltip
                                value={
                                    props.isDefault
                                        ? t("UserRoles.table.canNotDeleteDefaultRole")
                                        : props.isManager
                                          ? t("UserRoles.table.canNotDeleteManagerRole")
                                          : undefined
                                }
                            >
                                {t("Common.delete")}
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownContent>
            </DropdownMenu>

            {!isRestructuredRoleManagament && editRoleModal}
            <Modal isOpen={resultVisible} hideModal={hideResult} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResult}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            {!isRestructuredRoleManagament && cloneRoleModal}
            <Modal isOpen={resultVisible} hideModal={hideResult} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResult}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={deleteRoleFormVisible}
                hideModal={() => setDeleteRoleFormVisible(false)}
                modalTitle={t("ManageRoleForm.deleteRole.title")}
            >
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {deleteRoleFormVisible ? (
                            <>
                                <div className={style.resultContainer}>
                                    {t("ManageRoleForm.deleteRole.introductionMessage", { name: props.name })}
                                </div>
                                <div className={style.resultContainer}>
                                    {t("ManageRoleForm.deleteRole.confirmationMessage", { name: props.name })}
                                </div>
                                <div className={style.resultContainer}>
                                    <input
                                        id="confirmInput"
                                        type="text"
                                        data-testid={testIds.common.confirmationDialog.confirmInput}
                                        onChange={handleConfirmation}
                                    />
                                </div>

                                <div className={buttons.buttonContainer}>
                                    <button
                                        className={buttons.secondaryButtonWithoutIcon}
                                        onClick={() => setDeleteRoleFormVisible(false)}
                                        data-testid={testIds.common.dialog.closeButton}
                                    >
                                        {t("Common.cancel")}
                                    </button>

                                    <button
                                        className={buttons.primaryButtonWithoutIcon}
                                        data-testid={testIds.common.confirmationDialog.confirmButton}
                                        onClick={handleRoleDelete}
                                        disabled={confirmationState}
                                    >
                                        {t("Common.delete")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </>
                )}
            </Modal>
            <Modal isOpen={resultVisible} hideModal={hideResultAndRedirectToRoot} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResultAndRedirectToRoot}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
};
